import { useCallback, useEffect, useMemo, useState } from "react";
import { downloadArticleExport } from "@/lib/articleExcel";
import { api } from "@/lib/apiClient";
import { formatApiError } from "@/lib/formatApiError";
import { useCurrentUser } from "@/lib/auth";
import { UNIT_LABELS, formatTaxRatePercent } from "@/lib/articleConstants";
import { formatIsoDateShort } from "@/lib/bookingDates";
import { formatGrossFromNet } from "@/lib/money";
import { canWriteStammdaten } from "@/lib/permissions";
import { usePageHeader } from "@/layout/usePageHeader";
import { Button } from "@/components/ui/Button";
import { ClickableTableRow } from "@/components/ui/ClickableTableRow";
import { Table } from "@/components/ui/Table";
import { ArticleImportModal } from "@/pages/stammdaten/ArticleImportModal";
import { ArticlePricesModal } from "@/pages/stammdaten/ArticlePricesModal";
import { CreateArticleModal } from "@/pages/stammdaten/CreateArticleModal";
import { EditArticleModal } from "@/pages/stammdaten/EditArticleModal";
import { StammdatenWriteHint } from "@/pages/stammdaten/StammdatenWriteHint";
import type { Article } from "@/types/camping";

export function ArticlesPage() {
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canWrite = canWriteStammdaten(groups);
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingArticle, setEditingArticle] = useState<Article | null>(null);
  const [priceArticle, setPriceArticle] = useState<Article | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get<Article[]>("/api/v1/articles?include_inactive=true")
      .then(setArticles)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setPageMeta({ title: "Stammdaten", subtitle: "Artikel & Preise" });
    load();
    return () => setPageMeta(null);
  }, [load, setPageMeta]);

  async function onExport() {
    setExporting(true);
    setExportError(null);
    try {
      await downloadArticleExport(true);
    } catch (err) {
      setExportError(formatApiError(err, "Export fehlgeschlagen"));
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="space-y-6">
      <EditArticleModal
        open={!!editingArticle}
        article={editingArticle}
        onClose={() => setEditingArticle(null)}
        onSaved={load}
        canEdit={canWrite}
      />
      <CreateArticleModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={load}
      />
      <ArticleImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={load}
      />
      <ArticlePricesModal
        open={!!priceArticle}
        article={priceArticle}
        onClose={() => setPriceArticle(null)}
        onSaved={load}
        canEdit={canWrite}
      />

      {!canWrite ? <StammdatenWriteHint /> : null}

      <div className="flex flex-wrap justify-end gap-2">
        <Button
          type="button"
          variant="secondary"
          className="w-full sm:w-auto"
          loading={exporting}
          onClick={() => void onExport()}
        >
          Excel exportieren
        </Button>
        {canWrite ? (
          <Button
            type="button"
            variant="secondary"
            className="w-full sm:w-auto"
            onClick={() => setImportOpen(true)}
          >
            Excel importieren
          </Button>
        ) : null}
        {canWrite ? (
          <Button type="button" className="w-full sm:w-auto" onClick={() => setCreateOpen(true)}>
            Neuer Artikel
          </Button>
        ) : null}
      </div>

      {exportError ? <p className="text-sm text-status-red">{exportError}</p> : null}

      {error ? <p className="text-sm text-status-red">{error}</p> : null}

      {loading ? (
        <p className="text-sm text-label-secondary">Laden…</p>
      ) : (
        <Table wide emptyMessage="Noch keine Artikel." isEmpty={articles.length === 0}>
          <thead>
            <tr>
              <th>Nr.</th>
              <th>Kategorie</th>
              <th>Bezeichnung</th>
              <th>Lieferant</th>
              <th>Einheit</th>
              <th title="Kommt vom Buchungskonto des Artikels">MwSt.</th>
              <th>Aktueller Preis (brutto)</th>
              <th>gültig ab</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {articles.map((a) => (
              <ClickableTableRow
                key={a.id}
                ariaLabel={`Artikel ${a.article_number} bearbeiten`}
                onActivate={() => setEditingArticle(a)}
              >
                <td className="font-medium font-mono text-sm">{a.article_number}</td>
                <td className="text-label-secondary">{a.category_name ?? "—"}</td>
                <td>
                  <span className={a.is_active ? undefined : "text-label-secondary line-through"}>
                    {a.name}
                  </span>
                  {a.requires_pickup_receipt ? (
                    <span className="ml-2 rounded bg-fill-tertiary px-1.5 py-0.5 text-xs text-label-secondary">
                      Abholbeleg
                    </span>
                  ) : null}
                  {a.for_guest_shop ? (
                    <span className="ml-2 rounded bg-brand-50 px-1.5 py-0.5 text-xs text-brand-700">
                      Gäste-Shop
                    </span>
                  ) : null}
                </td>
                <td className="text-sm text-label-secondary">{a.supplier_name ?? "—"}</td>
                <td>{UNIT_LABELS[a.unit] ?? a.unit}</td>
                <td className="tabular-nums">{formatTaxRatePercent(a.tax_rate_percent)} %</td>
                <td className="tabular-nums">
                  {formatGrossFromNet(a.current_price, a.tax_rate_percent)}
                </td>
                <td className="text-label-secondary">
                  {a.current_price_valid_from
                    ? formatIsoDateShort(a.current_price_valid_from)
                    : "—"}
                </td>
                <td>
                  <button
                    type="button"
                    className="text-sm text-accent hover:underline"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPriceArticle(a);
                    }}
                  >
                    Preise
                  </button>
                </td>
              </ClickableTableRow>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}
